import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  Injector,
  signal,
  computed,
  input,
  output,
  effect,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePickerDirective } from '../../shared/directives';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { HuxleyComponent } from '../huxley/huxley';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';
import { InfoTipComponent } from '../info-tip/info-tip';
import { FrameworkIconComponent } from '../framework-icon/framework-icon';
import { PickerSkeletonComponent } from '../picker-skeleton/picker-skeleton';
import { FrameworkTemplatePickerComponent } from '../framework-template-picker/framework-template-picker';
import { LanguagePickerComponent } from '../language-picker/language-picker';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { ControlRefComponent } from '../control-ref/control-ref';
import { matchFrameworkIdentity, templateFrameworkIdentity } from '../../utils/framework.utils';
import type { ExistingControlMatch } from '../../models/import.model';

import {
  ControlRecord,
  ControlTemplate,
  isStrictFramework,
  FrameworkCreateRequest,
  FrameworkRecord,
  FrameworkTemplate,
  FrameworkType,
  EntityLanguage,
  Organization,
} from '../../models';
import { AuthService } from '../../services/auth/auth';
import { StateStoreService } from '../../services/state-store/state-store';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import {
  buildImportSummary,
  createFrameworkImportPickerHandler,
} from '../../shared/framework-import-picker-handler';
import { deriveSelectionState } from '../../shared/picker-handler';
import { MAX_CONTROL_IDS_PER_REQUEST } from '../../shared/framework-control-limits';
import {
  createFrameworkTemplatePickerHandler,
  type FrameworkTemplatePickerHandler,
} from '../../shared/framework-template-picker.handler';
import type { FrameworkTemplateWithUsage } from '../../utils/framework.utils';
import { formatControlSubtitle } from '../../utils/control.utils';
import { accessDenialMessage } from '../../utils/access-error.utils';
import { templatePickerText } from '../../utils/control-template.utils';
import { isFrameworkAlreadyExistsError } from '../../utils/error.utils';
import { getEntityLanguageLabel } from '../../utils/language.utils';

type ModalStep = 'language' | 'pick' | 'details' | 'import' | 'review';

@Component({
  selector: 'app-create-framework-modal',
  templateUrl: './create-framework-modal.html',
  styleUrl: './create-framework-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    DatePickerDirective,
    ModalComponent,
    ModalStatusComponent,
    HuxleyComponent,
    StepIndicatorComponent,
    InfoTipComponent,
    FrameworkIconComponent,
    PickerSkeletonComponent,
    FrameworkTemplatePickerComponent,
    LanguagePickerComponent,
    LanguageBadgeComponent,
    ControlRefComponent,
  ],
})
export class CreateFrameworkModal {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly stateStore = inject(StateStoreService);
  private readonly api = inject(OrgManagementApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly injector = inject(Injector);
  private readonly translationVersion = signal(0);

  readonly isVisible = input(false);
  readonly frameworkCreated = output<FrameworkRecord>();
  readonly cancelled = output<void>();

  // Step machine. Pick is always first — the user chooses a framework
  // template before anything else, so the family-derived branches (Type II
  // dates, predefined-controls flag) are decided before the Details form
  // renders. The Import step is then only relevant when the framework
  // family has no platform control templates (e.g. ISO 27001 ships 93
  // Annex A controls hard-wired to each revision, so importing from
  // another framework would just duplicate work) AND the caller is a
  // manager (the BE /controls/import endpoint is manager-gated).
  private readonly stepLabels = computed(() => {
    this.translationVersion();
    const t = (k: string) => this.transloco.translate(`modals.create_framework.${k}`);
    return {
      language: this.transloco.translate('language_step.step_label'),
      pick: t('step_pick'),
      details: t('step_details'),
      import: t('step_import'),
      review: t('step_review'),
    };
  });

  readonly activeStepConfigs = computed<StepConfig[]>(() => {
    const labels = this.stepLabels();
    // Language MUST be first — the pick step fetches framework templates
    // whose names + descriptions come in the selected language, and the
    // predefined-controls materialisation on Review depends on it too.
    // Locking the choice up-front keeps the whole downstream flow
    // consistent (no jarring "your review preview refetched because you
    // changed language at the end" moment).
    const steps: StepConfig[] = [
      {
        key: 'language',
        label: labels.language,
        detail: getEntityLanguageLabel(this.templateLanguage()),
      },
      {
        key: 'pick',
        label: labels.pick,
        detail: this.selectedTypeLabel() || undefined,
      },
      { key: 'details', label: labels.details },
    ];
    if (this.isManager() && !this.selectedTemplateHasPredefinedControls()) {
      steps.push({
        key: 'import',
        label: labels.import,
        detail: this.selectedSourceFrameworkName() || undefined,
      });
    }
    steps.push({ key: 'review', label: labels.review });
    return steps;
  });

  private readonly _currentStep = signal<ModalStep>('language');
  readonly currentStep = this._currentStep.asReadonly();

  /**
   * Highest step ever reached in this session. Advances on forward
   * navigation; never rolls back when the user jumps to an earlier step
   * via the timeline or the Back button. Reset only when the modal
   * closes (`resetModalState`) so the next open starts fresh.
   *
   * Drives the step-indicator's "visited but not current" state so
   * previously-completed steps keep their ✓ marker after backward
   * navigation — the check reflects what the user has *done*, not
   * where they currently are.
   */
  private readonly _maxReachedStep = signal<ModalStep>('language');
  readonly maxReachedStep = this._maxReachedStep.asReadonly();

  /**
   * Central setter that pairs `_currentStep` with `_maxReachedStep`
   * bookkeeping. Every forward transition should route through this so
   * backward navigation (Back button or timeline click) never loses the
   * visited state.
   */
  private setStep(next: ModalStep): void {
    this._currentStep.set(next);
    const steps = this.activeStepConfigs();
    const nextIdx = steps.findIndex(s => s.key === next);
    const maxIdx = steps.findIndex(s => s.key === this._maxReachedStep());
    if (nextIdx > maxIdx) {
      this._maxReachedStep.set(next);
    }
  }

  /**
   * Timeline click — user asked to jump to a previously-visited step.
   * The step-indicator has already gated to visited-and-not-current,
   * but defense-in-depth verify here too. No side effects: the reactive
   * form, staged imports (`_pendingImport`), snapshotted import
   * controls (`_pendingImportControls`), the fetched predefined
   * templates, and the coverage-preview cache all persist across
   * click-navigation, so the user can revisit any step to look or
   * edit without losing captured work.
   */
  protected onStepClicked(key: string): void {
    if (this.isTerminalState()) return;
    const steps = this.activeStepConfigs();
    const clickedIdx = steps.findIndex(s => s.key === key);
    const maxIdx = steps.findIndex(s => s.key === this._maxReachedStep());
    if (clickedIdx < 0 || clickedIdx > maxIdx) return;
    this.clearError();
    this._currentStep.set(key as ModalStep);
  }

  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);
  /** True when {@link error} is a permission answer rather than a failure —
   *  swaps the red banner for the amber-lock one. A refused write is the
   *  system working, and red says something broke. */
  private readonly _errorIsDenial = signal(false);
  private readonly _successMessage = signal<string | null>(null);
  private _successTimer: ReturnType<typeof setTimeout> | null = null;
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly errorIsDenial = this._errorIsDenial.asReadonly();
  readonly successMessage = this._successMessage.asReadonly();

  /** Clear both halves together. The pair is only ever written through
   *  these two helpers, so the flag cannot outlive the message it describes
   *  and leave a stale amber banner on the next real failure. */
  private clearError(): void {
    this._error.set(null);
    this._errorIsDenial.set(false);
  }

  /**
   * Set the banner from a failed request.
   *
   * The chokepoint every call here goes through: a 403 becomes the server's
   * localised denial and flips the banner amber; anything else keeps the
   * server's prose, then `fallback`.
   */
  private setErrorFrom(err: unknown, fallback: string): void {
    const denial = accessDenialMessage(err, this.transloco);
    this._errorIsDenial.set(denial !== null);
    this._error.set(
      denial ??
        (err as { error?: { message?: string } })?.error?.message ??
        (err as { message?: string })?.message ??
        fallback
    );
  }

  /** Set a banner we composed ourselves — never a denial. */
  private setError(message: string): void {
    this._errorIsDenial.set(false);
    this._error.set(message);
  }

  readonly isTerminalState = computed(() => this._isLoading() || !!this._successMessage());

  // Org context — managers operate on the TopNav-picked customer; non-
  // managers operate on their own selected org. Single resolution, used
  // throughout the wizard.
  readonly isManager = this.stateStore.isManager;
  readonly selectedOrg = computed<Organization | null>(() => {
    const id = this.isManager()
      ? this.stateStore.managedCustomerOrgId()
      : (this.stateStore.selectedOrgId() ?? null);
    if (!id) return null;
    return this.stateStore.organizations().find(o => o.principal_id === id) ?? null;
  });

  readonly currentUserName = computed(() => {
    this.translationVersion();
    const user = this.authService.user();
    if (user?.given_name && user?.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user?.email ?? this.transloco.translate('modals.create_control.current_user_fallback');
  });

  readonly templatePickerHandler: FrameworkTemplatePickerHandler =
    createFrameworkTemplatePickerHandler({
      api: this.api,
      orgId: () => this.selectedOrg()?.principal_id ?? null,
      injector: this.injector,
    });

  private readonly frameworkTemplateById = computed(() => {
    const map = new Map<string, FrameworkTemplate>();
    for (const t of this.templatePickerHandler.templates()) map.set(t.id, t);
    return map;
  });

  selectFrameworkTemplate(template: FrameworkTemplateWithUsage): void {
    if (template.inUse) return;
    this.clearError();
    const previousTemplateId = this.form.controls.framework_template_id.value;
    const templateChanged = previousTemplateId !== template.id;
    this.form.controls.framework_template_id.setValue(template.id);
    if (!this.form.controls.name.value?.trim()) {
      this.form.controls.name.setValue(template.name);
    }
    // Selecting a DIFFERENT template can reshape the wizard — a
    // predefined-controls family (ISO 27001) skips the Import step, so
    // a maxReached of 'import'/'review' may reference a step that no
    // longer exists. Roll max back to Details so the user re-walks
    // forward through the (possibly reshaped) flow.
    //
    // Re-picking the SAME template (user clicked back to Pick just to
    // look) preserves max — nothing downstream is invalidated.
    if (templateChanged) {
      this._maxReachedStep.set('details');
    }
    this.setStep('details');
  }

  readonly form = this.fb.group({
    framework_template_id: [null as string | null, Validators.required],
    name: ['', Validators.required],
    description: [''],
    target_date: [''],
    audit_date: [''],
    audit_period_start: [''],
  });

  // ─── Import wizard step (manager-only) ──────────────────────────
  // The handler is the same factory that powers the framework detail panel's
  // Import flow — the two surfaces share cross-org listing, filtering, and
  // selection semantics. Here we ONLY collect a selection; the actual API
  // call happens after framework creation succeeds (see `submitFramework`).
  readonly importHandler = createFrameworkImportPickerHandler({
    api: this.api,
    organizations: () => this.stateStore.organizations(),
    // Manager has selected a customer org by the time the import step opens.
    // Cross-org imports land in that customer org once the framework exists.
    targetOrgId: () => this.selectedOrg()?.principal_id ?? null,
    // No framework exists yet — no current framework to exclude from sources.
    excludeFrameworkId: () => null,
    // No pre-linked controls (the framework hasn't been created yet).
    alreadyLinkedControlIds: () => [],
  });
  readonly importPicker = this.importHandler.picker;
  /** Persisted across the import → review → submit transitions. */
  private readonly _pendingImport = signal<{
    source_org_id: string;
    source_framework_id: string;
    control_ids: string[];
  } | null>(null);
  readonly pendingImport = this._pendingImport.asReadonly();
  /**
   * Snapshot of the full control objects picked on the Import step, taken
   * when the user advances to Review. Decouples the Review list from the
   * import handler's `sourceFrameworkControls` signal so the list survives
   * navigating Back → Pick → Details and forward again (which could
   * conceivably reset the handler's source-framework state).
   */
  private readonly _pendingImportControls = signal<ControlRecord[]>([]);
  readonly pendingImportControls = this._pendingImportControls.asReadonly();
  /** Surfaces a warning if the framework was created but the import call failed. */
  private readonly _importWarning = signal<string | null>(null);
  readonly importWarning = this._importWarning.asReadonly();
  /** Success message for the import (shown on the success screen). */
  private readonly _importSummary = signal<string | null>(null);
  readonly importSummary = this._importSummary.asReadonly();

  readonly selectedSourceFrameworkName = computed(() => {
    const id = this._pendingImport()?.source_framework_id;
    if (!id) return null;
    return this.importHandler.importableFrameworks().find(f => f.framework_id === id)?.name ?? id;
  });

  readonly maxControlsPerRequest = MAX_CONTROL_IDS_PER_REQUEST;
  readonly formatControlSubtitle = formatControlSubtitle;

  private readonly importPickerVisibleIds = computed(() =>
    this.importHandler.filteredSourceFrameworkControls().map(c => c.control_id)
  );

  readonly importPickerSelectionState = computed(() =>
    deriveSelectionState(this.importPickerVisibleIds(), this.importPicker.selectedIds())
  );

  toggleImportPickerAll(): void {
    this.importPicker.toggleAll(this.importPickerVisibleIds());
  }

  // Public so the template can read the current selection and render
  // the matching `<app-framework-icon>` in the select trigger.
  readonly frameworkTemplateIdValue = toSignal(
    this.form.controls.framework_template_id.valueChanges,
    { initialValue: null as string | null }
  );
  private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
    initialValue: '',
  });
  private readonly formValue = toSignal(this.form.valueChanges);

  /** Currently-selected framework template, derived from the form's id field. */
  readonly selectedTemplate = computed<FrameworkTemplate | null>(() => {
    const id = this.frameworkTemplateIdValue();
    return id ? (this.frameworkTemplateById().get(id) ?? null) : null;
  });

  /** The framework family of the selected revision — drives the icon + the
   *  Type II conditional dates section. */
  readonly frameworkTypeValue = computed<FrameworkType | null>(
    () => this.selectedTemplate()?.framework_type ?? null
  );

  readonly isTypeII = computed(() => {
    const type = this.frameworkTypeValue();
    return type === FrameworkType.ISAE_3402_TYPE_II || type === FrameworkType.ISAE_3000_TYPE_II;
  });

  readonly selectedTypeLabel = computed(() => this.selectedTemplate()?.name ?? '');

  /**
   * Does the currently-selected framework family carry platform control
   * templates? Sourced from `isStrictFramework`
   * (single source of truth co-located with the family enum). Today only
   * ISO 27001 returns true — its 93 Annex A controls are seeded by the V013
   * migration and FK-linked to the framework_template revision by V015.
   *
   * Drives:
   *   1. Step visibility — the "Import controls from existing framework"
   *      step is hidden because the 93 controls are a hard requirement,
   *      not an optional source set.
   *   2. The Review step's summary line — "X standard controls will be
   *      created" appears instead of the import summary.
   *   3. Post-create work — `submitFramework` materialises the templates
   *      via `POST /controls/from-templates` instead of running the
   *      cross-framework import call.
   */
  readonly selectedTemplateHasPredefinedControls = computed<boolean>(() => {
    const type = this.frameworkTypeValue();
    return isStrictFramework(type);
  });

  /**
   * Platform control templates for the selected framework revision, fetched
   * once when the user picks a predefined-controls framework. Surfaced on
   * the Review step so the user sees exactly how many controls will be
   * materialised before they confirm.
   */
  private readonly _predefinedTemplates = signal<ControlTemplate[]>([]);
  private readonly _predefinedTemplatesLoading = signal(false);
  readonly predefinedTemplatesLoading = this._predefinedTemplatesLoading.asReadonly();

  // ── Coverage preview ────────────────────────────────────────────
  // When the wizard reaches Review with a predefined-controls
  // framework, we ask the BE which of those templates the org
  // already covers. The BE's ``POST /controls/from-templates``
  // materialise endpoint already deduplicates by ``template_id`` at
  // write time (existing control → LINKED to the new framework
  // instead of a duplicate INSERT), so this is purely a
  // *transparency* layer — the user should see the accurate
  // "N new + M linked" split BEFORE they commit, not read a flat
  // "93 will be created" line that hides the reality.
  //
  // Reuses `getExistingControlsForTemplates` (the same endpoint the
  // composite-reuse-panel drives) — no new BE endpoint invented,
  // no duplicate matching logic.
  private readonly _coverageMatches = signal<ExistingControlMatch[]>([]);
  private readonly _coverageLoading = signal(false);
  private readonly _coverageError = signal<string | null>(null);
  readonly coverageLoading = this._coverageLoading.asReadonly();
  readonly coverageError = this._coverageError.asReadonly();

  /**
   * Per-row view-model for the Review-step template list. Each row
   * pairs a template with the existing control that will be linked
   * to the new framework instead of a fresh insert (or `null` if the
   * template has no coverage).
   *
   * BE contract note: `covers_component_refs[]` on each candidate
   * carries the *template_reference* strings (e.g. "A.5.18") the
   * candidate covers — not template IDs. We cross-reference by
   * `control_reference` so the map is stable whether the BE built
   * the candidate list via `template_id` lineage or composite
   * junction rows. First-match-wins per ref (the BE returns one
   * candidate per template for the singleton case; composite
   * candidates that cover multiple refs each map to their first
   * uncovered ref in insertion order).
   *
   * Follows the "list view-model" convention from `CLAUDE.md`: bake
   * the transformation into a computed row, template consumes the
   * baked field, no per-row helper calls in the loop.
   */
  readonly templateRows = computed<
    readonly {
      template: ControlTemplate;
      linkedMatch: ExistingControlMatch | null;
      displayName: string;
      rationalePreview: string | null;
    }[]
  >(() => {
    const templates = this._predefinedTemplates();
    const matches = this._coverageMatches();
    const lang = this.templateLanguage();
    const byRef = new Map<string, ExistingControlMatch>();
    for (const match of matches) {
      for (const ref of match.covers_component_refs ?? []) {
        if (!byRef.has(ref)) byRef.set(ref, match);
      }
    }
    return templates.map(t => ({
      template: t,
      linkedMatch: t.control_reference ? (byRef.get(t.control_reference) ?? null) : null,
      ...templatePickerText(t, lang),
    }));
  });

  /** Summary counts derived from `templateRows`. Null while templates
   *  haven't loaded (parallel to the loading state — the banner then
   *  shows the loading/error variants). */
  readonly coverageCounts = computed<{ newCount: number; linkedCount: number } | null>(() => {
    const rows = this.templateRows();
    if (rows.length === 0) return null;
    const linkedCount = rows.filter(r => r.linkedMatch !== null).length;
    return {
      newCount: rows.length - linkedCount,
      linkedCount,
    };
  });

  /** Template bindings so `<app-control-ref>` in the row loop can
   *  resolve per-row identity + tooltip data without inline helper
   *  invocations. */
  protected readonly templateFrameworkIdentity = templateFrameworkIdentity;
  protected readonly matchFrameworkIdentity = matchFrameworkIdentity;

  /** Language for materialised control names/rationale. Initialised from app
   *  locale on each modal open; the user can override on the Review step. */
  readonly templateLanguage = signal<EntityLanguage>(this.stateStore.orgDefaultContentLanguage());

  onTemplateLanguageChange(lang: EntityLanguage): void {
    this.templateLanguage.set(lang);
  }

  readonly detailsValid = computed(() => {
    const template = this.selectedTemplate();
    const name = this.nameValue();
    return !!template && !!name && name.trim().length > 0;
  });

  readonly modalConfig = computed<ModalConfig>(() => {
    this.translationVersion();
    const step = this._currentStep();
    const terminal = this.isTerminalState();
    const t = (k: string) => this.transloco.translate(`modals.create_framework.${k}`);

    // Shared shell. The customer name lives in the header-meta badge
    // (driven by `showManagedCustomerContext`); a duplicated `\u2014 ${orgName}`
    // suffix on the title would be redundant.
    const base = {
      title: t('modal_title'),
      size: 'medium' as const,
      showCloseButton: !terminal,
      showFooter: !terminal,
      showLogo: true,
      showManagedCustomerContext: !terminal,
    };

    if (step === 'language') {
      // `templateLanguage` is signal-initialised to the org default and
      // never nullable, so Next is always enabled here.
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_cancel'),
      };
    }

    if (step === 'pick') {
      // No primary action — clicking a framework row IS the commit. A
      // disabled "Next" would be dead UI chrome. Back returns to the
      // language step; Cancel is on Language (the first step).
      return {
        ...base,
        secondaryButtonText: t('secondary_back'),
      };
    }

    if (step === 'details') {
      return {
        ...base,
        primaryButtonText: t('primary_next'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: !this.detailsValid(),
      };
    }

    if (step === 'import') {
      // Optional step \u2014 the primary button reads "Skip" when nothing is
      // picked, "Next" once the user has selected controls. We disable it
      // (rather than silently truncating) when the picker selection exceeds
      // the BE cap so the error surface is the same Next button the user is
      // already focused on.
      const currentSize = this.importPicker.selectedIds().size;
      const hasSelection = currentSize > 0;
      const overCap = currentSize > MAX_CONTROL_IDS_PER_REQUEST;
      return {
        ...base,
        primaryButtonText: hasSelection ? t('primary_next') : t('primary_skip'),
        secondaryButtonText: t('secondary_back'),
        primaryButtonDisabled: overCap,
      };
    }

    const loading = this._isLoading();
    return {
      ...base,
      primaryButtonText: loading ? t('primary_creating') : t('primary_create'),
      secondaryButtonText: t('secondary_back'),
      primaryButtonDisabled: loading,
      primaryButtonLoading: loading,
    };
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    effect(() => {
      if (!this.isVisible()) {
        this.resetModalState();
      }
    });

    // Refresh on open so a create in another tab is reflected before the
    // picker latches onto a stale template.
    effect(() => {
      if (this.isVisible()) {
        this._currentStep.set('language');
        this._maxReachedStep.set('language');
        this.templateLanguage.set(this.stateStore.orgDefaultContentLanguage());
        this.templatePickerHandler.reload();
      }
    });

    // Fetch the platform control templates as soon as a predefined-controls
    // framework template is picked. Triggered reactively off the form's
    // `framework_template_id` value so re-selecting a different revision
    // refetches against that revision's UUID (version-precise). Single
    // round-trip per selection; the list is bounded (~93 rows for
    // ISO 27001:2022) and lives in `_predefinedTemplates` until reset.
    let lastFetchedTemplateId: string | null = null;
    effect(() => {
      const templateId = this.frameworkTemplateIdValue();
      const hasPredefined = this.selectedTemplateHasPredefinedControls();
      if (!templateId || !hasPredefined) {
        // Selection cleared, or this family carries no predefined controls.
        this._predefinedTemplates.set([]);
        this._coverageMatches.set([]);
        this._coverageError.set(null);
        lastFetchedTemplateId = null;
        return;
      }
      if (templateId === lastFetchedTemplateId) return;
      lastFetchedTemplateId = templateId;
      this._predefinedTemplatesLoading.set(true);
      this.api
        .getControlTemplates({ framework_template_id: templateId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: templates => {
            this._predefinedTemplates.set(templates);
            this._predefinedTemplatesLoading.set(false);
          },
          error: err => {
            this._predefinedTemplatesLoading.set(false);
            this.setErrorFrom(
              err,
              this.transloco.translate('modals.create_framework.error_predefined_failed')
            );
          },
        });
    });

    // Coverage-preview fetch. Fires once the predefined templates
    // arrive AND the wizard is on the Review step (matches the surface
    // that renders the banner — no wasted round-trip if the user
    // clicks Cancel before Review). Reuses the composite-reuse-panel
    // endpoint (`getExistingControlsForTemplates`) so the FE never
    // implements a second matching primitive. Guarded by
    // ``lastCoverageKey`` so wandering forward and back in the wizard
    // doesn't refetch on every step change.
    let lastCoverageKey: string | null = null;
    effect(() => {
      const step = this._currentStep();
      const templates = this._predefinedTemplates();
      const orgId = this.selectedOrg()?.principal_id ?? null;
      if (step !== 'review' || templates.length === 0 || !orgId) {
        return;
      }
      const templateIds = templates.map(t => t.id);
      const key = `${orgId}::${templateIds.join(',')}`;
      if (key === lastCoverageKey) return;
      lastCoverageKey = key;
      this._coverageLoading.set(true);
      this._coverageError.set(null);
      this.api
        .getExistingControlsForTemplates(orgId, templateIds)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: matches => {
            this._coverageMatches.set(matches);
            this._coverageLoading.set(false);
          },
          error: err => {
            this._coverageLoading.set(false);
            this._coverageError.set(
              err?.error?.message ||
                err?.message ||
                this.transloco.translate('modals.create_framework.error_coverage_failed')
            );
          },
        });
    });
  }

  onContinue(): void {
    const step = this._currentStep();
    this.clearError();

    if (step === 'language') {
      this.setStep('pick');
      return;
    }

    if (step === 'details') {
      // Managers without a predefined-controls family see the optional
      // Import step; members and predefined-flow callers skip straight
      // to Review (the 93 ISO controls aren't user-selectable).
      const showImport = this.isManager() && !this.selectedTemplateHasPredefinedControls();
      if (showImport) {
        this.goToImportStep();
      } else {
        this.setStep('review');
      }
      return;
    }
    if (step === 'import') {
      // Stash the current picker selection (or null if nothing picked).
      const selection = this.importHandler.getSelection();
      this._pendingImport.set(selection);
      // Snapshot the matching control objects so the Review step can
      // render their names/refs/domains without re-querying — and stays
      // correct even if the user navigates away and back.
      if (selection) {
        const ids = new Set(selection.control_ids);
        this._pendingImportControls.set(
          this.importHandler.sourceFrameworkControls().filter(c => ids.has(c.control_id))
        );
      } else {
        this._pendingImportControls.set([]);
      }
      this.setStep('review');
      return;
    }
    if (step === 'review') {
      this.submitFramework();
    }
  }

  /**
   * Enter the Import step. Unlike the drawer (where clicking the "Import"
   * button calls `importPicker.open()` which kicks off the load via the
   * `onOpen` hook), wizard navigation only sets the step — so the
   * frameworks list has to be loaded explicitly here. Idempotent: skips
   * the fetch once the list is populated or already in flight, so re-
   * visiting the step (via Back from Review) doesn't refetch or wipe
   * the user's in-progress selection.
   */
  private goToImportStep(): void {
    this.setStep('import');
    if (
      !this.importHandler.importableFrameworks().length &&
      !this.importHandler.loadingImportableFrameworks()
    ) {
      this.importHandler.loadImportableFrameworks();
    }
  }

  onDismiss(): void {
    if (this.isTerminalState()) return;
    this.cancelled.emit();
  }

  onBack(): void {
    if (this.isTerminalState()) return;
    this.clearError();
    const step = this._currentStep();

    if (step === 'review') {
      // Mirror the forward navigation: jump back to Import only when that
      // step is actually part of the active flow. Predefined-controls
      // families and non-managers go straight back to Details.
      const showImport = this.isManager() && !this.selectedTemplateHasPredefinedControls();
      if (showImport) {
        this.goToImportStep();
      } else {
        this._currentStep.set('details');
      }
      return;
    }

    if (step === 'import') {
      this._currentStep.set('details');
      return;
    }

    if (step === 'details') {
      this._currentStep.set('pick');
      return;
    }

    if (step === 'pick') {
      this._currentStep.set('language');
      return;
    }

    // Already on Language (the first step) — Back means cancel.
    this.cancelled.emit();
  }

  readonly hasUnsavedWork = computed(() => {
    this.formValue(); // track form changes so dirty state is reactive
    // Language + Pick are zero-cost starting states; nothing committed
    // yet. Past that, OR the moment a framework has been picked (which
    // is set programmatically and so doesn't mark the form dirty on its
    // own), we have meaningful work-in-progress worth warning about on
    // close.
    const step = this._currentStep();
    if (
      step === 'language' ||
      (step === 'pick' && !this.form.controls.framework_template_id.value)
    ) {
      return this.form.dirty;
    }
    return true;
  });

  private submitFramework(): void {
    if (!this.detailsValid()) return;

    const orgId = this.selectedOrg()?.principal_id ?? this.stateStore.selectedOrgId();
    if (!orgId) return;

    const raw = this.form.getRawValue();
    const request: FrameworkCreateRequest = {
      org_id: orgId,
      framework_template_id: raw.framework_template_id!,
      name: raw.name!,
      description: raw.description || undefined,
      target_date: raw.target_date || undefined,
      audit_date: raw.audit_date || undefined,
      audit_period_start: raw.audit_period_start || undefined,
      language: this.templateLanguage(),
    };

    this._isLoading.set(true);
    this.clearError();
    this._importWarning.set(null);
    this._importSummary.set(null);

    this.api
      .createFramework(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: framework => this.afterFrameworkCreated(framework),
        error: err => {
          this._isLoading.set(false);
          if (isFrameworkAlreadyExistsError(err)) {
            this.templatePickerHandler.reload();
            // The template the user picked is no longer valid — reset both
            // current and max so the timeline forces them to re-walk from
            // the picker rather than jumping straight to a stale Review.
            this._currentStep.set('pick');
            this._maxReachedStep.set('pick');
            // Language stays locked — the user's language choice is
            // still valid even if the framework template collided; we
            // just want them to reselect the template.
            this.setError(
              this.transloco.translate(
                'modals.create_framework.errors.framework_already_exists_for_template'
              )
            );
            return;
          }
          this.setErrorFrom(
            err,
            this.transloco.translate('modals.create_framework.error_create_failed')
          );
        },
      });
  }

  /**
   * After the framework exists, chain the follow-up that brings its
   * controls into the new framework:
   *
   * - Predefined-controls families (ISO 27001 today) → materialise the
   *   platform's `templates.control_templates` for the framework's pinned
   *   revision via `POST /controls/from-templates`. Hard requirement, not
   *   user-selectable; we always send the full set of template_ids fetched
   *   on the details step.
   * - Other families → if the user picked controls on the Import step,
   *   chain the existing cross-framework import.
   * - Neither → finish with the plain "created" success message.
   *
   * Either follow-up failing does NOT roll back the framework — the user
   * can retry from the framework detail panel, and rolling back would
   * trade a recoverable failure for an unrecoverable lost framework.
   */
  private afterFrameworkCreated(framework: FrameworkRecord): void {
    if (this.selectedTemplateHasPredefinedControls()) {
      const templateIds = this._predefinedTemplates().map(t => t.id);
      if (templateIds.length === 0) {
        // Loaded list was empty — finish with the short message rather than
        // hanging on a no-op call.
        this.finishWithSuccess(
          framework,
          this.transloco.translate('modals.create_framework.success_created')
        );
        return;
      }
      this.api
        .materialiseControlsFromTemplates(framework.org_id, {
          framework_id: framework.framework_id,
          template_ids: templateIds,
          language: this.templateLanguage(),
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            const totalLinked = res.materialised.length + (res.already_present?.length ?? 0);
            this._importSummary.set(
              this.transloco.translate('modals.create_framework.predefined_summary', {
                count: totalLinked,
              })
            );
            this.finishWithSuccess(
              framework,
              this.transloco.translate('modals.create_framework.success_created_with_predefined', {
                count: totalLinked,
              })
            );
          },
          error: err => {
            console.error('Framework created but predefined materialise failed:', err);
            this._importWarning.set(
              this.transloco.translate('modals.create_framework.predefined_warning')
            );
            this.finishWithSuccess(
              framework,
              this.transloco.translate('modals.create_framework.success_created_short')
            );
          },
        });
      return;
    }

    const pending = this._pendingImport();
    if (!pending) {
      this.finishWithSuccess(
        framework,
        this.transloco.translate('modals.create_framework.success_created')
      );
      return;
    }

    this.api
      .importControlsToFramework(framework.org_id, framework.framework_id, pending)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this._importSummary.set(buildImportSummary(res));
          this.finishWithSuccess(
            framework,
            this.transloco.translate('modals.create_framework.success_created_and_imported')
          );
        },
        error: err => {
          console.error('Framework created but import failed:', err);
          this._importWarning.set(
            this.transloco.translate('modals.create_framework.import_warning')
          );
          this.finishWithSuccess(
            framework,
            this.transloco.translate('modals.create_framework.success_created_short')
          );
        },
      });
  }

  private finishWithSuccess(framework: FrameworkRecord, message: string): void {
    this._isLoading.set(false);
    this._successMessage.set(message);
    this._successTimer = setTimeout(() => this.frameworkCreated.emit(framework), 1500);
  }

  private resetModalState(): void {
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
    this.form.reset({
      framework_template_id: null,
      name: '',
      description: '',
      target_date: '',
      audit_date: '',
      audit_period_start: '',
    });
    this.clearError();
    this._isLoading.set(false);
    this._successMessage.set(null);
    this._currentStep.set('language');
    this._maxReachedStep.set('language');
    this.templatePickerHandler.search.set('');

    this.importHandler.close();
    this._pendingImport.set(null);
    this._pendingImportControls.set([]);
    this._importWarning.set(null);
    this._importSummary.set(null);
    this._predefinedTemplates.set([]);
    this._predefinedTemplatesLoading.set(false);
    this._coverageMatches.set([]);
    this._coverageLoading.set(false);
    this._coverageError.set(null);
    this.templateLanguage.set(this.stateStore.orgDefaultContentLanguage());
  }
}
